import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue, get } from "firebase/database";
import { Eye, Download, ArrowLeft, Info, X, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./documents.css";

const TransportDocuments = () => {
  const [orderDocuments, setOrderDocuments] = useState([]);
  const [fullOrders, setFullOrders] = useState({});
  const [transporterProfiles, setTransporterProfiles] = useState({});
  const [clientProfiles, setClientProfiles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const db = getDatabase();
    let docsRef = ref(db, "transport_documents");
    let ordersRef = ref(db, "transport_orders");
    let transporterProfileRef = ref(db, "transporter-profile");
    let clientProfileRef = ref(db, "profileDashboard");

    let unsubDocs, unsubOrders;

    // Load transporter and client profiles
    Promise.all([get(transporterProfileRef), get(clientProfileRef)]).then(
      ([transporterSnap, clientSnap]) => {
        const transporterMap = {};
        const clientMap = {};
        if (transporterSnap.exists()) {
          Object.entries(transporterSnap.val() || {}).forEach(
            ([tid, tprofile]) => {
              transporterMap[tid] = tprofile;
            }
          );
        }
        if (clientSnap.exists()) {
          Object.entries(clientSnap.val() || {}).forEach(
            ([cid, cprofile]) => {
              clientMap[cid] = cprofile;
            }
          );
        }
        setTransporterProfiles(transporterMap);
        setClientProfiles(clientMap);

        // Listen for transport_documents after profiles are loaded
        unsubDocs = onValue(docsRef, (snapshot) => {
          const docs = snapshot.val() || {};
          const result = [];
          Object.entries(docs).forEach(([transporterId, orders]) => {
            Object.entries(orders || {}).forEach(([orderId, docTypes]) => {
              // Read common fields from docTypes
              const {
                clientName = "",
                transporterName = "",
                from = "",
                to = "",
                quantity = "",
                quantityUnit = "",
                createdAt = "",
                orderId: docOrderId = orderId,
              } = docTypes;
              // Parse PODs only from docTypes.POD
              const pods = [];
              if (docTypes.POD) {
                Object.entries(docTypes.POD).forEach(([podId, pod]) => {
                  pods.push({
                    id: podId,
                    driverNumber: pod.driverNumber || "",
                    truckNumber: pod.truckNumber || "",
                    load: pod.load || "",
                    fileUrl: pod.fileUrl || "",
                    uploadedAt: pod.uploadedAt || "",
                  });
                });
              }
              // Parse invoices only from docTypes.invoices
              const invoices = [];
              if (docTypes.invoices) {
                Object.entries(docTypes.invoices).forEach(([invId, invoice]) => {
                  invoices.push({
                    id: invId,
                    amount: invoice.amount || "",
                    billNumber: invoice.billNumber || "",
                    billDate: invoice.billDate || "",
                    fileUrl: invoice.fileUrl || "",
                    uploadedAt: invoice.uploadedAt || "",
                  });
                });
              }
              pods.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));
              invoices.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));
              // Parse payments
              const payments = [];
              if (docTypes.payments) {
                Object.entries(docTypes.payments).forEach(([paymentId, payment]) => {
                  payments.push({
                    id: paymentId,
                    amount: payment.amount || "",
                    utrNumber: payment.utrNumber || "",
                    paymentDate: payment.paymentDate || "",
                    fileUrl: payment.fileUrl || "",
                    uploadedAt: payment.uploadedAt || "",
                  });
                });
              }
              payments.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));
              result.push({
                orderId: docOrderId,
                transporterId,
                transporterName: transporterName || "Unknown",
                clientName: clientName || "Unknown",
                from,
                to,
                quantity,
                quantityUnit,
                createdAt,
                pods,
                invoices,
                payments,
              });
            });
          });
          setOrderDocuments(result);
          setIsLoading(false);
        });
      }
    );

    // Listen for transport_orders (flatten nested structure)
    unsubOrders = onValue(ordersRef, (snapshot) => {
      const orders = snapshot.val() || {};
      // Flatten: transporterId -> orderId -> orderObj
      const orderMap = {};
      Object.entries(orders).forEach(([transporterId, transporterOrders]) => {
        if (typeof transporterOrders === "object" && transporterOrders !== null) {
          Object.entries(transporterOrders).forEach(([orderKey, orderObj]) => {
            // Use orderObj.orderId if present, else fallback to orderKey
            const oid = orderObj && orderObj.orderId ? orderObj.orderId : orderKey;
            orderMap[oid] = { ...orderObj, transporterId, orderId: oid };
          });
        }
      });
      setFullOrders(orderMap);
    });

    // Cleanup
    return () => {
      if (typeof unsubDocs === "function") unsubDocs();
      if (typeof unsubOrders === "function") unsubOrders();
    };
    // eslint-disable-next-line
  }, []);

  // Helper: Download file from url
  const handleDownload = (url, name = "document") => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper: Open details popup using document data from orderDocuments
  const openOrderDetail = (orderId, transporterId) => {
    const doc = orderDocuments.find(
      (d) => d.orderId === orderId && d.transporterId === transporterId
    );
    if (!doc) return;
    setSelectedOrderDetail(doc);
    setShowDetailPopup(true);
  };

  // Filtered documents by search
  const filteredDocuments = orderDocuments.filter((doc) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return (
      (doc.orderId && doc.orderId.toLowerCase().includes(s)) ||
      (doc.clientName && doc.clientName.toLowerCase().includes(s)) ||
      (doc.transporterName && doc.transporterName.toLowerCase().includes(s))
    );
  });

  return (
    <div className="user-pod-container">
      <div className="user-pod-header">
       
        <h2>Transport Documents</h2>
        <button
          className="user-pod-info"
          onClick={() => setShowInfo(true)}
          title="Info"
        >
          <Info size={22} />
        </button>
      </div>
      <div className="user-pod-searchbar">
        <Search size={18} className="mb-3" />
        <input
          type="text"
          placeholder="Search by Order ID, Client, or Transporter"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {showInfo && (
        <div className="user-pod-modal">
          <div className="user-pod-modal-content">
            <button
              className="user-pod-modal-close"
              onClick={() => setShowInfo(false)}
            >
              <X size={20} />
            </button>
            <h3>Transport Documents</h3>
            <p>
              This page allows admins to view and download Proof of Delivery (POD) and invoice documents submitted by transporters for all orders.
              <br />
              Use the search bar to quickly find orders by order ID, client, or transporter.
              <br />
              Click "View More" to see order details.
            </p>
          </div>
        </div>
      )}
      {isLoading ? (
        <div className="user-pod-empty">Loading...</div>
      ) : filteredDocuments.length === 0 ? (
        <div className="user-pod-empty">No Proof of Delivery found.</div>
      ) : (
        <div className="user-pod-grid">
          {filteredDocuments.map((doc) => (
            <div className="user-pod-card" key={doc.orderId + doc.transporterId}>
              <div className="user-pod-card-header">
                <div>
                  <div className="user-pod-card-title">
                    <span style={{ fontWeight: 600 }}>Order ID:</span> <span>{doc.orderId}</span>
                  </div>
                  <div className="user-pod-card-route">
                    <span style={{ fontWeight: 600 }}>Route:</span>{" "}
                    <span>{doc.from && doc.to ? `${doc.from} → ${doc.to}` : "-"}</span>
                  </div>
                  <div className="user-pod-card-client">
                    <span style={{ fontWeight: 600 }}>Client:</span>{" "}
                    <span>{doc.clientName || "-"}</span>
                  </div>
                </div>
                <button
                  className="user-pod-view-btn"
                  title="View Details"
                  onClick={() => openOrderDetail(doc.orderId, doc.transporterId)}
                >
                  <Eye size={20} />
                </button>
              </div>
              <div className="user-pod-card-row2">
                <div>
                  <span style={{ fontWeight: 600 }}>Quantity:</span>{" "}
                  <span>
                    {doc.quantity || "-"} {doc.quantityUnit || ""}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: 600 }}>Transporter:</span>{" "}
                  <span>{doc.transporterName || "-"}</span>
                </div>
              </div>
              <div className="user-pod-card-badges">
                <div className="user-pod-badge-row">
                  <span className="user-pod-badge">
                    PODs: <b>{doc.pods.length}</b> Uploaded
                  </span>
                  <br />
                  <span className="user-pod-badge">
                    Invoices: <b>{doc.invoices.length}</b> Uploaded
                  </span>
                </div>
                <div className="user-pod-badge-row">
                  <span className="user-pod-badge">
                    Payment: <b>{doc.payments?.length ? "Submitted" : "Not Submitted"}</b>
                  </span>
                </div>
              </div>
              <div className="user-pod-card-section">
                {/* Optionally show a compact summary or preview here if desired */}
              </div>
            </div>
          ))}
        </div>
      )}
      {showDetailPopup && selectedOrderDetail && (
        <div className="user-pod-modal">
          <div className="user-pod-modal-content user-pod-detail-popup">
            <button
              className="user-pod-modal-close"
              onClick={() => setShowDetailPopup(false)}
            >
              <X size={20} />
            </button>
            <h3>Order Details</h3>
            <div className="user-pod-detail-section">
              <div className="user-pod-detail-summary">
                <div><strong>Order ID:</strong> {selectedOrderDetail.orderId}</div>
                <div><strong>Client:</strong> {selectedOrderDetail.clientName || "-"}</div>
                <div><strong>Transporter:</strong> {selectedOrderDetail.transporterName || "-"}</div>
                <div><strong>Quantity:</strong> {selectedOrderDetail.quantity || "-"} {selectedOrderDetail.quantityUnit || ""}</div>
                <div><strong>Route:</strong> {selectedOrderDetail.from && selectedOrderDetail.to ? `${selectedOrderDetail.from} → ${selectedOrderDetail.to}` : "-"}</div>
                <div><strong>Created:</strong> {selectedOrderDetail.createdAt ? new Date(selectedOrderDetail.createdAt).toLocaleString() : "-"}</div>
              </div>
            </div>
            {/* POD table */}
            <div className="user-pod-detail-table-section">
              <h4 style={{ marginTop: 24, marginBottom: 8 }}>PODs</h4>
              <div className="user-pod-detail-table-scroll">
                <table className="user-pod-detail-table">
                  <thead>
                    <tr>
                      <th>Driver Number</th>
                      <th>Truck Number</th>
                      <th>Load</th>
                      <th>Uploaded Date</th>
                      <th>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrderDetail.pods && selectedOrderDetail.pods.length > 0 ? (
                      selectedOrderDetail.pods.map((pod) => (
                        <tr key={pod.id}>
                          <td>{pod.driverNumber || "-"}</td>
                          <td>{pod.truckNumber || "-"}</td>
                          <td>{pod.load || "-"}</td>
                          <td>
                            {pod.uploadedAt
                              ? new Date(pod.uploadedAt).toLocaleString()
                              : "-"}
                          </td>
                          <td>
                            {pod.fileUrl ? (
                              <button
                                className="user-pod-download-btn"
                                title="Download POD"
                                onClick={() => window.open(pod.fileUrl, "_blank")}
                              >
                                <Download size={18} />
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center" }}>
                          No PODs uploaded
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Invoice table */}
            <div className="user-pod-detail-table-section">
              <h4 style={{ marginTop: 24, marginBottom: 8 }}>Invoices</h4>
              <div className="user-pod-detail-table-scroll">
                <table className="user-pod-detail-table">
                  <thead>
                    <tr>
                      <th>Bill Number</th>
                      <th>Bill Date</th>
                      <th>Amount</th>
                      <th>Uploaded Date</th>
                      <th>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrderDetail.invoices && selectedOrderDetail.invoices.length > 0 ? (
                      selectedOrderDetail.invoices.map((inv) => (
                        <tr key={inv.id}>
                          <td>{inv.billNumber || "-"}</td>
                          <td>
                            {inv.billDate
                              ? new Date(inv.billDate).toLocaleDateString()
                              : "-"}
                          </td>
                          <td>{inv.amount || "-"}</td>
                          <td>
                            {inv.uploadedAt
                              ? new Date(inv.uploadedAt).toLocaleString()
                              : "-"}
                          </td>
                          <td>
                            {inv.fileUrl ? (
                              <button
                                className="user-pod-download-btn"
                                title="Download Invoice"
                                onClick={() => window.open(inv.fileUrl, "_blank")}
                              >
                                <Download size={18} />
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center" }}>
                          No invoices uploaded
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Payments table */}
            <div className="user-pod-detail-table-section">
              <h4 style={{ marginTop: 24, marginBottom: 8 }}>Payments</h4>
              <div className="user-pod-detail-table-scroll">
                <table className="user-pod-detail-table">
                  <thead>
                    <tr>
                      <th>Payment Date</th>
                      <th>UTR Number</th>
                      <th>Amount</th>
                      <th>Uploaded Date</th>
                      <th>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrderDetail.payments && selectedOrderDetail.payments.length > 0 ? (
                      selectedOrderDetail.payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            {payment.paymentDate
                              ? new Date(payment.paymentDate).toLocaleDateString()
                              : "-"}
                          </td>
                          <td>{payment.utrNumber || "-"}</td>
                          <td>{payment.amount || "-"}</td>
                          <td>
                            {payment.uploadedAt
                              ? new Date(payment.uploadedAt).toLocaleString()
                              : "-"}
                          </td>
                          <td>
                            {payment.fileUrl ? (
                              <button
                                className="user-pod-download-btn"
                                title="Download Payment"
                                onClick={() => window.open(payment.fileUrl, "_blank")}
                              >
                                <Download size={18} />
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center" }}>
                          No payments uploaded
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportDocuments 